import React, { useState, useMemo, useContext, useEffect, useCallback, useRef } from "react";
import { Footer, Navbar, PromoCodeSection } from "../components";
import { useSelector, useDispatch } from "react-redux";
import { setCart } from "../redux/action";
import { Link, useNavigate } from "react-router-dom";
import api from "../utils/base_url";
import { clearCart } from "../redux/reducer/handleCart";
import { AuthContext } from "../context/AuthContext";
import { PaymentContext } from "../context/PaymentContext";
import toast from "react-hot-toast";
import '../styles/index.css';

const isPhoneOrPlaceholderName = (name) => {
  if (!name || typeof name !== "string") return true;
  const clean = name.trim();
  if (["user", "customer", "admin", "anonymous", "null", "none", "undefined"].includes(clean.toLowerCase())) {
    return true;
  }
  const numericOnly = clean.replace(/[\s+\-()]/g, "");
  return /^\d{7,15}$/.test(numericOnly);
};

const Checkout = () => {
  const cartItems = useSelector((state) => state.handleCart);
  const { user, setUser } = useContext(AuthContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fieldRefs = useRef({});

  const { setPendingOrderId } = useContext(PaymentContext);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [loadingPincode, setLoadingPincode] = useState(false);

  // ✅ applied coupon state (persisted across Cart and Checkout)
  const [appliedCoupon, setAppliedCoupon] = useState(() => {
    try {
      const saved = sessionStorage.getItem("applied_coupon");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const appliedCouponRef = useRef(appliedCoupon);
  useEffect(() => {
    appliedCouponRef.current = appliedCoupon;
  }, [appliedCoupon]);

  // ✅ single source of truth for all pricing — replaces `shipping` state
  const [priceSummary, setPriceSummary] = useState(null);

  // ✅ tracks the very first subtotal-only fetch (no state yet) so
  // "Product Amount" never renders as 0 while waiting for address/pincode
  const [loadingSummary, setLoadingSummary] = useState(true);

  const [courierReady, setCourierReady] = useState(false);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    contact_number: "",
    secondary_number: "",

    billing_door: "",
    billing_street: "",
    billing_area: "",
    billing_landmark: "",
    billing_city: "",
    billing_state: "",
    billing_pincode: "",

    shipping_door: "",
    shipping_street: "",
    shipping_area: "",
    shipping_landmark: "",
    shipping_city: "",
    shipping_state: "",
    shipping_pincode: "",

    sameAsBilling: false,
    paymentMethod: "online",

    billing_manual_entry: false,
    shipping_manual_entry: false,
  });

  const [errors, setErrors] = useState({});

  // ─── Razorpay script ───────────────────────────────────────────────
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  // ✅ Stable callback — does NOT depend on appliedCoupon object reference
  const fetchPriceSummary = useCallback(async (state = "", couponCode = undefined) => {
    if (!cartItems.length) return;

    const activeCouponCode = couponCode !== undefined
      ? couponCode
      : appliedCouponRef.current?.code;

    try {
      if (state) setLoadingShipping(true);
      else setLoadingSummary(true);

      const payload = {
        items: cartItems.map((item) => ({ product: item.id, quantity: item.qty })),
        state: state || "",
        customer_id: user?.customer_details?.id || user?.id || null,
      };
      if (activeCouponCode && activeCouponCode.trim()) {
        payload.coupon_code = activeCouponCode.trim().toUpperCase();
      }

      const res = await api.post("cart/price-summary/", payload);

      setPriceSummary(res.data);
      setCourierReady(!!res.data.totals?.courier_ready);

      // If coupon failed in backend evaluation
      if (appliedCouponRef.current && res.data.totals?.coupon_applied === false && res.data.totals?.coupon_error) {
        toast.error(res.data.totals.coupon_error, { icon: "⚠️" });
        setAppliedCoupon(null);
        sessionStorage.removeItem("applied_coupon");
      } else if (appliedCouponRef.current && res.data.totals?.discount_amount !== undefined) {
        const newDiscount = Number(res.data.totals.discount_amount);
        setAppliedCoupon((prev) => {
          if (!prev) return null;
          if (prev.discount_amount === newDiscount) return prev;
          return { ...prev, discount_amount: newDiscount };
        });
      }
    } catch (err) {
      if (appliedCouponRef.current && err.response?.data?.message) {
        toast.error(`Coupon removed: ${err.response.data.message}`);
        setAppliedCoupon(null);
        sessionStorage.removeItem("applied_coupon");
      }
      setPriceSummary(null);
      setCourierReady(false);
    } finally {
      if (state) setLoadingShipping(false);
      setLoadingSummary(false);
    }
  }, [cartItems, user?.customer_details?.id, user?.id]);

  const handleApplyCoupon = (couponData) => {
    setAppliedCoupon(couponData);
    sessionStorage.setItem("applied_coupon", JSON.stringify(couponData));
    const activeState = formData.shipping_state || formData.billing_state || "";
    fetchPriceSummary(activeState, couponData.code);
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    sessionStorage.removeItem("applied_coupon");
    const activeState = formData.shipping_state || formData.billing_state || "";
    fetchPriceSummary(activeState, "");
  };

  // ✅ fires as soon as cart items are available (on mount / refresh)
  useEffect(() => {
    fetchPriceSummary();
  }, [fetchPriceSummary]);

  // ─── Pincode → city/state lookup ───────────────────────────────────
  const fetchAddressFromPincode = async (pincode, type) => {
    if (!/^\d{6}$/.test(pincode)) return;

    try {
      setLoadingPincode(true);

      const response = await fetch(
        `https://pincode.scaleupstores.com/api/v1/pincode/lookup/${pincode}/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-API-KEY": "my_secure_api_key_2026",
          },
        }
      );

      const data = await response.json();

      // SUCCESS
      if (data.status) {
        setFormData((prev) => ({
          ...prev,
          [`${type}_city`]: data.district || "",
          [`${type}_state`]: data.state || "",
          [`${type}_manual_entry`]: false,
        }));

        setErrors((prev) => ({
          ...prev,
          [`${type}_city`]: "",
          [`${type}_state`]: "",
          [`${type}_pincode`]: "",
        }));

        if (data.state) {
          setCourierReady(false);
          await fetchPriceSummary(data.state);
        }

        return;
      }

      // NOT FOUND
      toast.error("Pincode not found. Please enter manually.");

      setCourierReady(false);

      setFormData((prev) => ({
        ...prev,
        [`${type}_city`]: "",
        [`${type}_state`]: "",
        [`${type}_manual_entry`]: true,
      }));

    } catch (error) {
      console.error(error);
      toast.error("Pincode service unavailable. Please enter manually.");

      setCourierReady(false);

      setFormData((prev) => ({
        ...prev,
        [`${type}_city`]: "",
        [`${type}_state`]: "",
        [`${type}_manual_entry`]: true,
      }));

    } finally {
      setLoadingPincode(false);
    }
  };

  // ─── Parse saved address string ────────────────────────────────────
  const parseAddress = (address = "") => {
    const parts = address.split(",").map((p) => p.trim());
    let city = "", state = "", pincode = "";

    parts.forEach((p) => {
      const match = p.match(/(.+)\s*-\s*(\d{6})/);
      if (match) { state = match[1].trim(); pincode = match[2]; }
    });

    const pinIndex = parts.findIndex((p) => p.includes(pincode));
    if (pinIndex > 0) city = parts[pinIndex - 1];

    return {
      door: parts[0] || "",
      street: parts[1] || "",
      area: parts[2] || "",
      landmark: parts.find((p) => p.toLowerCase().startsWith("landmark"))
        ?.replace("Landmark:", "").trim() || "",
      city,
      state,
      pincode,
    };
  };

  // ─── Checkout stock validation ──────────────────────────────────────
  const validateCheckout = useCallback(async () => {
    try {
      const res = await api.post("checkout-validate/", { user_id: user.id });
      if (!res.data.status) {
        const qtyMap = {};
        res.data.valid_items.forEach((i) => (qtyMap[i.id] = i.qty));
        const newCart = cartItems
          .map((item) => qtyMap[item.id] !== undefined ? { ...item, qty: qtyMap[item.id] } : null)
          .filter(Boolean);
        dispatch(setCart(newCart));
        toast.error("Cart updated due to stock/reservation changes");
        navigate("/cart");
      }
    } catch {
      toast.error("Unable to validate checkout");
      navigate("/cart");
    }
  }, [cartItems, dispatch, navigate, user.id]);

  useEffect(() => { validateCheckout(); }, [validateCheckout]);

  // ─── Preload saved address & contact ONCE when customer ID is available ──────
  useEffect(() => {
    const customerId = user?.customer_details?.id;
    if (!customerId) return;

    let isMounted = true;
    (async () => {
      try {
        const res = await api.get(`/customerslist/${customerId}/`);
        if (!isMounted) return;
        const data = res.data?.data || {};

        // Only prefill name if it's a real name (not a phone number or placeholder)
        const rawFullName = data.full_name || user?.customer_details?.full_name || "";
        const isRealName = !isPhoneOrPlaceholderName(rawFullName);
        const nameParts = isRealName ? rawFullName.trim().split(/\s+/) : [];
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        const parsed = data.address ? parseAddress(data.address) : null;

        setFormData((prev) => ({
          ...prev,
          first_name: prev.first_name || firstName,
          last_name: prev.last_name || lastName,
          contact_number: prev.contact_number || data.contact_number || user?.phone || user?.customer_details?.contact_number || "",
          secondary_number: prev.secondary_number || data.secondary_number || "",

          ...(parsed ? {
            billing_door: prev.billing_door || parsed.door,
            billing_street: prev.billing_street || parsed.street,
            billing_area: prev.billing_area || parsed.area,
            billing_landmark: prev.billing_landmark || parsed.landmark,
            billing_city: prev.billing_city || parsed.city,
            billing_state: prev.billing_state || parsed.state,
            billing_pincode: prev.billing_pincode || parsed.pincode,

            shipping_door: prev.shipping_door || parsed.door,
            shipping_street: prev.shipping_street || parsed.street,
            shipping_area: prev.shipping_area || parsed.area,
            shipping_landmark: prev.shipping_landmark || parsed.landmark,
            shipping_city: prev.shipping_city || parsed.city,
            shipping_state: prev.shipping_state || parsed.state,
            shipping_pincode: prev.shipping_pincode || parsed.pincode,
          } : {}),
        }));

        if (parsed && /^\d{6}$/.test(parsed.pincode)) {
          setCourierReady(false);
          fetchPriceSummary(parsed.state);
        }
      } catch (err) {
        console.error("Failed to load saved address", err);
      }
    })();

    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.customer_details?.id]);

  // ─── Form field change ─────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value, checked } = e.target;

    if (
      (name === "contact_number" || name === "secondary_number") &&
      value !== "" && !/^\d+$/.test(value)
    ) return;

    if (name === "sameAsBilling") {
      setFormData((prev) => ({
        ...prev,
        sameAsBilling: checked,
        shipping_door: checked ? prev.billing_door : "",
        shipping_street: checked ? prev.billing_street : "",
        shipping_area: checked ? prev.billing_area : "",
        shipping_landmark: checked ? prev.billing_landmark : "",
        shipping_city: checked ? prev.billing_city : "",
        shipping_state: checked ? prev.billing_state : "",
        shipping_pincode: checked ? prev.billing_pincode : "",
        shipping_manual_entry: checked
          ? prev.billing_manual_entry
          : false,
      }));
    } else {
      setFormData((prev) => {
        const updated = {
          ...prev,
          [name]: value,
        };

        if (
          name === "billing_state" &&
          prev.billing_manual_entry &&
          value.trim().length > 2
        ) {
          setCourierReady(false);
          fetchPriceSummary(value.trim());
        }

        if (
          name === "shipping_state" &&
          prev.shipping_manual_entry &&
          value.trim().length > 2
        ) {
          setCourierReady(false);
          fetchPriceSummary(value.trim());
        }

        return updated;
      });
    }

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // ─── Order totals — sourced entirely from the API, no local math ────
  const totals = priceSummary?.totals;
  const originalSubtotal = totals?.original_subtotal != null
    ? Number(totals.original_subtotal)
    : (totals?.product_subtotal != null ? Number(totals.product_subtotal) : 0);

  const productDiscount = totals?.product_offer_savings != null
    ? Number(totals.product_offer_savings)
    : 0;

  const rawSubtotal = totals?.effective_subtotal != null
    ? Number(totals.effective_subtotal)
    : Math.max(0, originalSubtotal - productDiscount);

  const couponDiscount = totals?.discount_amount != null
    ? Number(totals.discount_amount)
    : (totals?.coupon_discount != null
      ? Number(totals.coupon_discount)
      : (appliedCoupon?.discount_amount ? Number(appliedCoupon.discount_amount) : 0));

  const discountedSubtotal = totals?.discounted_subtotal != null
    ? Number(totals.discounted_subtotal)
    : Math.max(0, rawSubtotal - couponDiscount);

  const gstAmount = totals?.gst_amount != null ? Number(totals.gst_amount) : null;
  const gstPercent = totals?.gst_percent != null ? Number(totals.gst_percent) : null;

  const totalSavings = totals?.total_savings != null
    ? Number(totals.total_savings)
    : (productDiscount + couponDiscount);

  const shippingCost = totals?.shipping_cost != null ? Number(totals.shipping_cost) : 0;
  const totalAmount = totals?.total_amount != null
    ? Number(totals.total_amount)
    : (courierReady ? (discountedSubtotal + shippingCost) : null);

  const totalItems = useMemo(
    () => cartItems.reduce((acc, item) => acc + item.qty, 0),
    [cartItems]
  );

  // ─── Validation ─────────────────────────────────────────────────────
  const validateForm = () => {
    const errors = {};

    if (!formData.first_name.trim()) errors.first_name = "First name is required";
    else if (!/^[A-Za-z ]{2,30}$/.test(formData.first_name)) errors.first_name = "Enter a valid first name";

    if (formData.last_name && !/^[A-Za-z ]{1,30}$/.test(formData.last_name))
      errors.last_name = "Enter a valid last name";

    if (!/^[6-9]\d{9}$/.test(formData.contact_number))
      errors.contact_number = "Enter valid 10-digit mobile number";

    if (formData.secondary_number && !/^[6-9]\d{9}$/.test(formData.secondary_number))
      errors.secondary_number = "Enter valid secondary number";

    if (!formData.billing_door.trim()) errors.billing_door = "House / Door No is required";
    if (!formData.billing_street.trim() || formData.billing_street.length < 3)
      errors.billing_street = "Street must be at least 3 characters";
    if (!formData.billing_area.trim() || formData.billing_area.length < 3)
      errors.billing_area = "Area must be at least 3 characters";
    if (formData.billing_landmark && formData.billing_landmark.length < 3)
      errors.billing_landmark = "Landmark must be at least 3 characters";
    if (!/^\d{6}$/.test(formData.billing_pincode))
      errors.billing_pincode = "Enter valid 6-digit pincode";
    if (!formData.billing_city?.trim()) {
      errors.billing_city = formData.billing_manual_entry
        ? "City is required"
        : "City not detected from pincode";
    }

    if (!formData.billing_state?.trim()) {
      errors.billing_state = formData.billing_manual_entry
        ? "State is required"
        : "State not detected from pincode";
    }

    if (!formData.sameAsBilling) {
      if (!formData.shipping_door.trim()) errors.shipping_door = "House / Door No is required";
      if (!formData.shipping_street.trim() || formData.shipping_street.length < 3)
        errors.shipping_street = "Street must be at least 3 characters";
      if (!formData.shipping_area.trim() || formData.shipping_area.length < 3)
        errors.shipping_area = "Area must be at least 3 characters";
      if (formData.shipping_landmark && formData.shipping_landmark.length < 3)
        errors.shipping_landmark = "Landmark must be at least 3 characters";
      if (!/^\d{6}$/.test(formData.shipping_pincode))
        errors.shipping_pincode = "Enter valid 6-digit pincode";
      if (!formData.shipping_city?.trim()) {
        errors.shipping_city = formData.shipping_manual_entry
          ? "City is required"
          : "City not detected from pincode";
      }

      if (!formData.shipping_state?.trim()) {
        errors.shipping_state = formData.shipping_manual_entry
          ? "State is required"
          : "State not detected from pincode";
      }
    }

    return errors;
  };

  // ─── Address strings ────────────────────────────────────────────────
  const billingFull = [
    formData.billing_door, formData.billing_street, formData.billing_area,
    formData.billing_landmark && `Landmark: ${formData.billing_landmark}`,
    `${formData.billing_city}, ${formData.billing_state} - ${formData.billing_pincode}`,
    "India",
  ].filter(Boolean).join(", ");

  const shippingFull = formData.sameAsBilling ? billingFull : [
    formData.shipping_door, formData.shipping_street, formData.shipping_area,
    formData.shipping_landmark && `Landmark: ${formData.shipping_landmark}`,
    `${formData.shipping_city}, ${formData.shipping_state} - ${formData.shipping_pincode}`,
    "India",
  ].filter(Boolean).join(", ");

  // ─── Smooth scroll to form field ──────────────────────────────────
  const scrollToField = (fieldName) => {
    const fieldEl = fieldRefs.current[fieldName];
    if (fieldEl) {
      fieldEl.scrollIntoView({ behavior: "smooth", block: "center" });
      const inputEl = fieldEl.querySelector ? fieldEl.querySelector("input, select, textarea") : fieldEl;
      if (inputEl) {
        inputEl.classList.add("highlight-field");
        setTimeout(() => {
          inputEl.classList.remove("highlight-field");
        }, 1200);
        setTimeout(() => {
          if (typeof inputEl.focus === "function") {
            inputEl.focus({ preventScroll: true });
          }
        }, 250);
      }
    }
  };

  // ─── Submit ─────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check pincode first: if pincode not entered, automatically navigate screen to pincode field
    const billingPincodeMissing = !formData.billing_pincode || !/^\d{6}$/.test(formData.billing_pincode.trim());
    const shippingPincodeMissing = !formData.sameAsBilling && (!formData.shipping_pincode || !/^\d{6}$/.test(formData.shipping_pincode.trim()));

    if (billingPincodeMissing) {
      setErrors((prev) => ({ ...prev, billing_pincode: "Enter valid 6-digit pincode" }));
      scrollToField("billing_pincode");
      toast.error("Please enter your pincode");
      return;
    }

    if (shippingPincodeMissing) {
      setErrors((prev) => ({ ...prev, shipping_pincode: "Enter valid 6-digit pincode" }));
      scrollToField("shipping_pincode");
      toast.error("Please enter shipping pincode");
      return;
    }

    const newErrors = validateForm();
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const firstErrorKey = Object.keys(newErrors)[0];
      scrollToField(firstErrorKey);
      toast.error("Please fix the highlighted fields");
      return;
    }

    if (!cartItems.length) { toast.error("Cart is empty!"); return; }

    const finalShippingState = formData.shipping_state || formData.billing_state;
    if (!finalShippingState || !courierReady) {
      toast.error("Please enter a valid serviceable pincode to calculate delivery charges");
      scrollToField(formData.sameAsBilling ? "billing_pincode" : (formData.shipping_pincode ? "shipping_pincode" : "billing_pincode"));
      return;
    }

    // 👤 Automatically update customer profile (name, address) if currently missing or set to phone number
    const enteredFullName = `${formData.first_name || ""} ${formData.last_name || ""}`.trim();
    const customerId = user?.customer_details?.id;
    const currentProfileName = user?.customer_details?.full_name || "";

    if (customerId && enteredFullName && isPhoneOrPlaceholderName(currentProfileName)) {
      try {
        const fd = new FormData();
        fd.append("full_name", enteredFullName);
        if (shippingFull && !user?.customer_details?.address) {
          fd.append("address", shippingFull);
        }
        await api.put(`customerdetails/${customerId}/`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        // Sync into AuthContext and sessionStorage immediately
        const updatedUser = {
          ...user,
          customer_details: {
            ...user.customer_details,
            full_name: enteredFullName,
            ...(shippingFull && !user?.customer_details?.address ? { address: shippingFull } : {}),
          },
        };
        sessionStorage.setItem("user", JSON.stringify(updatedUser));
        if (setUser) setUser(updatedUser);
      } catch (profileErr) {
        console.warn("Could not auto-update customer profile from checkout:", profileErr);
      }
    }

    const itemsPayload = cartItems.map((item) => ({ product: item.id, quantity: item.qty }));
    const orderPayload = {
      customer: user.customer_details.id,
      first_name: formData.first_name,
      last_name: formData.last_name,
      contact_number: formData.contact_number,
      secondary_number: formData.secondary_number,
      billing_address: billingFull,
      shipping_address: shippingFull,
      shipping_state: finalShippingState,
      payment_method: formData.paymentMethod,
      items: itemsPayload,
      coupon_code: appliedCoupon ? appliedCoupon.code : undefined,
    };

    // COD
    if (formData.paymentMethod === "cod") {
      try {
        const res = await api.post("orders/create-cod/", { order_data: orderPayload });
        toast.success("Order placed successfully!");
        sessionStorage.removeItem("applied_coupon");
        dispatch(clearCart());
        navigate(`/order-confirmation/${res.data.order.id}`);
      } catch (err) {
        toast.error(err.response?.data?.message || "Something went wrong!");
      }
      return;
    }

    // Online payment
    try {
      const reserveRes = await api.post("orders/reserve/", {
        items: itemsPayload,
        payment_method: "online",
        order_data: orderPayload,
        coupon_code: appliedCoupon ? appliedCoupon.code : undefined,
      });

      if (!reserveRes.data.status) { toast.error(reserveRes.data.message); return; }

      const razorpayOrder = reserveRes.data.razorpay_order;
      localStorage.setItem("pendingOrder", razorpayOrder.id);

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        order_id: razorpayOrder.id,
        name: "Vannakarangal",
        description: "Order Payment",
        handler: function (response) {
          sessionStorage.removeItem("applied_coupon");
          setProcessingPayment(false);
          toast.success("Payment successful! Confirming your order…");
          setPendingOrderId(razorpayOrder.id);
        },
        modal: {
          ondismiss: function (reason) {
            if (reason === undefined) {
              api.post("payments/log-frontend-event/", {
                razorpay_order_id: razorpayOrder.id,
                event_type: "USER_CANCELLED",
                error_details: { timestamp: new Date().toISOString() },
              }).catch(() => { });
              localStorage.removeItem("pendingOrder");
              toast.error("Payment cancelled.");
            } else if (reason === "timeout") {
              api.post("payments/log-frontend-event/", {
                razorpay_order_id: razorpayOrder.id,
                event_type: "PAYMENT_TIMEOUT",
                error_details: { timestamp: new Date().toISOString() },
              }).catch(() => { });
              localStorage.removeItem("pendingOrder");
              toast.error("Payment timed out. Please try again.");
            } else {
              toast.error("Payment failed. Please try again.");
            }
            setProcessingPayment(false);
          },
        },
        prefill: {
          name: `${formData.first_name} ${formData.last_name}`,
          contact: formData.contact_number,
        },
        theme: { color: "#4A1F1A" },
      };

      setProcessingPayment(true);
      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", (response) => {
        try { rzp.close(); } catch { }
        api.post("payments/log-frontend-event/", {
          razorpay_order_id: response.error.metadata.order_id,
          event_type: "PAYMENT_FAILED_FRONTEND",
          error_details: {
            payment_id: response.error.metadata.payment_id,
            error_code: response.error.code,
            error_description: response.error.description,
            error_reason: response.error.reason,
            timestamp: new Date().toISOString(),
          },
        }).catch(() => { });
        toast.error(response.error.description || "Payment failed");
        setProcessingPayment(false);
      });

      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to initiate payment");
    }
  };

  // ─── Empty cart guard ───────────────────────────────────────────────
  if (!cartItems.length) {
    return (
      <div className="container py-5 text-center">
        <h4 className="text-theme-dark">No items in Cart</h4>
        <Link to="/" className="btn btn-outline-themed mt-3">Continue Shopping</Link>
      </div>
    );
  }

  // ─── Shipping row helper ────────────────────────────────────────────
  const shippingDisplay = () => {
    if (loadingPincode || loadingShipping) {
      return (
        <>
          <span className="spinner-border spinner-border-sm me-1" role="status" />
          Calculating...
        </>
      );
    }
    if (!courierReady) {
      return (
        <span style={{ fontSize: "0.82rem", color: "#7a563a", fontStyle: "italic" }}>
          Enter pincode to calculate
        </span>
      );
    }
    return `₹${shippingCost.toFixed(2)}`;
  };

  // ─── Place order button label ───────────────────────────────────────
  const submitLabel = () => {
    if (processingPayment) return (
      <><span className="spinner-border spinner-border-sm me-2" />Processing Payment...</>
    );
    if (loadingPincode || loadingShipping) return (
      <><span className="spinner-border spinner-border-sm me-2" />Calculating courier...</>
    );
    if (!courierReady) return "Enter pincode to place order";
    return `Place Order (₹${totalAmount != null ? Math.round(totalAmount).toLocaleString() : Math.round(discountedSubtotal + shippingCost).toLocaleString()})`;
  };

  return (
    <>
      <Navbar />
      <div className="container py-5">
        <div className="row g-4">

          {/* ── Order Summary ── */}
          <div className="col-lg-4 col-md-5 order-md-last">
            {/* Promo Code Section */}
            <PromoCodeSection
              cartItems={cartItems}
              appliedCoupon={appliedCoupon}
              onApplyCoupon={handleApplyCoupon}
              onRemoveCoupon={handleRemoveCoupon}
              disabled={processingPayment || loadingShipping}
              shippingState={formData.shipping_state || formData.billing_state || ""}
              customerId={user?.customer_details?.id || user?.id || null}
            />

            <div className="card shadow-theme rounded-theme">
              <div className="card-header bg-light">
                <h5 className="m-0 text-theme-dark">Order Summary</h5>
              </div>
              <div className="card-body">
                <ul className="list-group list-group-flush">
                  {/* Item Total (MRP) */}
                  <li className="list-group-item d-flex justify-content-between">
                    <span>Item Total (MRP) ({totalItems})</span>
                    <strong>
                      {loadingSummary ? (
                        <span className="spinner-border spinner-border-sm" role="status" />
                      ) : (
                        `₹${(originalSubtotal || rawSubtotal).toFixed(2)}`
                      )}
                    </strong>
                  </li>

                  {/* Product Discount */}
                  {productDiscount > 0 && (
                    <li className="list-group-item d-flex justify-content-between text-success">
                      <span>Product Discount</span>
                      <strong>-₹{productDiscount.toFixed(2)}</strong>
                    </li>
                  )}

                  {/* Subtotal */}
                  <li className="list-group-item d-flex justify-content-between border-top pt-2">
                    <span className="fw-semibold">Subtotal</span>
                    <strong>
                      {loadingSummary ? "…" : `₹${rawSubtotal.toFixed(2)}`}
                    </strong>
                  </li>

                  {/* Coupon Discount */}
                  {couponDiscount > 0 && (
                    <li className="list-group-item d-flex justify-content-between text-success fw-semibold align-items-center">
                      <div className="d-flex align-items-center gap-1">
                        <span>Coupon ({appliedCoupon?.code || totals?.coupon_code || "PROMO"})</span>
                        <button
                          type="button"
                          onClick={handleRemoveCoupon}
                          className="btn btn-link text-danger p-0 ms-1 text-decoration-none"
                          style={{ fontSize: "0.78rem" }}
                          title="Remove coupon"
                        >
                          (Remove)
                        </button>
                      </div>
                      <strong>-₹{couponDiscount.toFixed(2)}</strong>
                    </li>
                  )}

                  {/* Estimated GST (Included) */}
                  {gstAmount != null && gstAmount > 0 && (
                    <li className="list-group-item d-flex justify-content-between text-muted" style={{ fontSize: "0.85rem" }}>
                      <span>Estimated GST {gstPercent ? `(${gstPercent}%)` : ""} (Included)</span>
                      <span>(₹{gstAmount.toFixed(2)})</span>
                    </li>
                  )}

                  {/* Delivery Charges */}
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    <span>Delivery Charges</span>
                    <strong>{shippingDisplay()}</strong>
                  </li>

                  {/* Total Payable */}
                  <li className="list-group-item d-flex justify-content-between fw-bold border-top pt-3" style={{ fontSize: "1.08rem" }}>
                    <span>Total Payable</span>
                    <strong className="text-theme-dark">
                      {courierReady
                        ? `₹${totalAmount != null ? totalAmount.toFixed(2) : (discountedSubtotal + shippingCost).toFixed(2)}`
                        : <span style={{ fontSize: "0.82rem", color: "#7a563a", fontStyle: "italic", fontWeight: 400 }}>
                          calculated after pincode
                        </span>
                      }
                    </strong>
                  </li>
                </ul>

                {/* Savings Highlight Banner */}
                {totalSavings > 0 && (
                  <div
                    className="mt-3 p-2 text-center rounded-3"
                    style={{
                      background: "#e8f5e9",
                      color: "#1b5e20",
                      fontSize: "0.86rem",
                      fontWeight: 600,
                      border: "1px dashed #2E7D32",
                    }}
                  >
                    🎉 You are saving <strong>₹{totalSavings.toFixed(2)}</strong> on this order!
                  </div>
                )}

                {!courierReady && !loadingShipping && !loadingPincode && (
                  <div
                    onClick={() => scrollToField(formData.sameAsBilling ? "billing_pincode" : (formData.shipping_pincode ? "shipping_pincode" : "billing_pincode"))}
                    style={{
                      marginTop: "12px",
                      padding: "10px 12px",
                      background: "#faeeda",
                      borderRadius: "8px",
                      fontSize: "0.8rem",
                      color: "#633806",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      cursor: "pointer",
                    }}
                    role="button"
                    title="Click to enter pincode"
                  >
                    <span style={{ marginTop: "1px" }}>ⓘ</span>
                    <span>Enter your pincode in the form below to see the delivery charge and final total.</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Billing & Shipping Form ── */}
          <div className="col-lg-8 col-md-7">
            <div className="card shadow-theme rounded-theme">
              <div className="card-header bg-light">
                <h5 className="m-0 text-theme-dark">Billing & Shipping</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit} noValidate className="checkout-form">
                  <div className="row g-3">

                    {/* First Name */}
                    <div ref={(el) => (fieldRefs.current.first_name = el)} className="col-sm-6">
                      <label className="form-label required">First Name</label>
                      <input type="text" name="first_name" maxLength="15"
                        className={`form-control themed-input ${errors.first_name ? "is-invalid" : ""}`}
                        value={formData.first_name} onChange={handleChange} required />
                      {errors.first_name && <div className="error-text">{errors.first_name}</div>}
                    </div>

                    {/* Last Name */}
                    <div className="col-sm-6">
                      <label className="form-label">Last Name</label>
                      <input type="text" name="last_name" maxLength="15"
                        className={`form-control themed-input ${errors.last_name ? "is-invalid" : ""}`}
                        value={formData.last_name} onChange={handleChange} />
                    </div>

                    {/* Contact Number */}
                    <div ref={(el) => (fieldRefs.current.contact_number = el)} className="col-sm-6">
                      <label className="form-label required">Contact Number</label>
                      <input type="tel" name="contact_number" maxLength="10"
                        className={`form-control themed-input ${errors.contact_number ? "is-invalid" : ""}`}
                        value={formData.contact_number} onChange={handleChange} required />
                      {errors.contact_number && <div className="error-text">{errors.contact_number}</div>}
                    </div>

                    {/* Secondary Number */}
                    <div className="col-sm-6">
                      <label className="form-label">Secondary Contact Number</label>
                      <input type="tel" name="secondary_number" minLength="10" maxLength="10"
                        className={`form-control themed-input ${errors.secondary_number ? "is-invalid" : ""}`}
                        value={formData.secondary_number} onChange={handleChange} />
                    </div>

                    {/* ── Billing Address ── */}
                    <h5>Billing Address</h5>

                    <div ref={(el) => (fieldRefs.current.billing_door = el)} className="col-sm-6">
                      <label className="form-label required">House / Flat / Door No</label>
                      <input type="text" name="billing_door" maxLength="30"
                        className={`form-control themed-input ${errors.billing_door ? "is-invalid" : ""}`}
                        value={formData.billing_door} onChange={handleChange} required />
                      {errors.billing_door && <div className="error-text">{errors.billing_door}</div>}
                    </div>

                    <div ref={(el) => (fieldRefs.current.billing_street = el)} className="col-sm-6">
                      <label className="form-label required">Street / Road Name</label>
                      <input type="text" name="billing_street" maxLength="50"
                        className={`form-control themed-input ${errors.billing_street ? "is-invalid" : ""}`}
                        value={formData.billing_street} onChange={handleChange} required />
                      {errors.billing_street && <div className="error-text">{errors.billing_street}</div>}
                    </div>

                    <div ref={(el) => (fieldRefs.current.billing_area = el)} className="col-sm-6">
                      <label className="form-label required">Area / Locality</label>
                      <input type="text" name="billing_area" maxLength="50"
                        className={`form-control themed-input ${errors.billing_area ? "is-invalid" : ""}`}
                        value={formData.billing_area} onChange={handleChange} required />
                      {errors.billing_area && <div className="error-text">{errors.billing_area}</div>}
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label">Landmark</label>
                      <input type="text" name="billing_landmark" maxLength="50"
                        className={`form-control themed-input ${errors.billing_landmark ? "is-invalid" : ""}`}
                        value={formData.billing_landmark} onChange={handleChange} />
                      {errors.billing_landmark && <div className="error-text">{errors.billing_landmark}</div>}
                    </div>

                    <div ref={(el) => (fieldRefs.current.billing_pincode = el)} className="col-sm-6">
                      <label className="form-label required">Pincode</label>
                      <input type="text" name="billing_pincode" minLength="6" maxLength="6"
                        className={`form-control themed-input ${errors.billing_pincode ? "is-invalid" : ""}`}
                        value={formData.billing_pincode}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (!/^\d*$/.test(value)) return;
                          setFormData((prev) => {
                            if (value.length < 6 && prev.sameAsBilling) {
                              setCourierReady(false);
                            }

                            return {
                              ...prev,
                              billing_pincode: value,
                              billing_city: "",
                              billing_state: "",
                              billing_manual_entry: false,
                            };
                          });
                          setErrors((prev) => ({
                            ...prev,
                            billing_pincode: "",
                            billing_city: "",
                            billing_state: "",
                          }));
                          if (value.length === 6) fetchAddressFromPincode(value, "billing");
                        }}
                        required />
                      {errors.billing_pincode && <div className="error-text">{errors.billing_pincode}</div>}
                    </div>

                    <div ref={(el) => (fieldRefs.current.billing_city = el)} className="col-sm-6">
                      <label className="form-label required">
                        City
                      </label>
                      <input type="text" name="billing_city"
                        maxLength="25"
                        className={`form-control themed-input ${errors.billing_city ? "is-invalid" : ""}`}
                        value={formData.billing_city} onChange={handleChange} disabled={!formData.billing_manual_entry} />
                      {errors.billing_city && <div className="error-text">{errors.billing_city}</div>}
                    </div>

                    <div ref={(el) => (fieldRefs.current.billing_state = el)} className="col-sm-6">
                      <label className="form-label required">State</label>
                      <input type="text" name="billing_state"
                        maxLength="25"
                        className={`form-control themed-input ${errors.billing_state ? "is-invalid" : ""}`}
                        value={formData.billing_state} onChange={handleChange} disabled={!formData.billing_manual_entry} />
                      {errors.billing_state && <div className="error-text">{errors.billing_state}</div>}
                    </div>

                    {/* Same as Billing checkbox */}
                    <div className="col-12 form-check my-2">
                      <input type="checkbox" className="form-check-input thick-checkbox"
                        name="sameAsBilling" checked={formData.sameAsBilling}
                        onChange={handleChange} id="sameAsBilling" />
                      <label htmlFor="sameAsBilling" className="form-check-label ms-2">
                        Shipping address same as billing
                      </label>
                    </div>

                    {/* ── Shipping Address ── */}
                    {!formData.sameAsBilling && (
                      <>
                        <h5>Shipping Address</h5>

                        <div ref={(el) => (fieldRefs.current.shipping_door = el)} className="col-sm-6">
                          <label className="form-label required">House / Flat / Door No</label>
                          <input type="text" name="shipping_door" minLength="3" maxLength="30"
                            className={`form-control themed-input ${errors.shipping_door ? "is-invalid" : ""}`}
                            value={formData.shipping_door} onChange={handleChange} required />
                          {errors.shipping_door && <div className="error-text">{errors.shipping_door}</div>}
                        </div>

                        <div ref={(el) => (fieldRefs.current.shipping_street = el)} className="col-sm-6">
                          <label className="form-label required">Street / Road Name</label>
                          <input type="text" name="shipping_street" minLength="3" maxLength="50"
                            className={`form-control themed-input ${errors.shipping_street ? "is-invalid" : ""}`}
                            value={formData.shipping_street} onChange={handleChange} required />
                          {errors.shipping_street && <div className="error-text">{errors.shipping_street}</div>}
                        </div>

                        <div ref={(el) => (fieldRefs.current.shipping_area = el)} className="col-sm-6">
                          <label className="form-label required">Area / Locality</label>
                          <input type="text" name="shipping_area" maxLength="50"
                            className={`form-control themed-input ${errors.shipping_area ? "is-invalid" : ""}`}
                            value={formData.shipping_area} onChange={handleChange} required />
                          {errors.shipping_area && <div className="error-text">{errors.shipping_area}</div>}
                        </div>

                        <div className="col-sm-6">
                          <label className="form-label">Landmark</label>
                          <input type="text" name="shipping_landmark" maxLength="50"
                            className={`form-control themed-input ${errors.shipping_landmark ? "is-invalid" : ""}`}
                            value={formData.shipping_landmark} onChange={handleChange} />
                          {errors.shipping_landmark && <div className="error-text">{errors.shipping_landmark}</div>}
                        </div>

                        <div ref={(el) => (fieldRefs.current.shipping_pincode = el)} className="col-sm-6">
                          <label className="form-label required">Pincode</label>
                          <input type="text" name="shipping_pincode" minLength="6" maxLength="6"
                            className={`form-control themed-input ${errors.shipping_pincode ? "is-invalid" : ""}`}
                            value={formData.shipping_pincode}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (!/^\d*$/.test(value)) return;
                              setFormData((prev) => ({
                                ...prev,
                                shipping_pincode: value,
                                shipping_city: "",
                                shipping_state: "",
                                shipping_manual_entry: false,
                              }));
                              if (value.length < 6) {
                                setCourierReady(false);
                              }
                              setErrors((prev) => ({
                                ...prev,
                                shipping_pincode: "",
                                shipping_city: "",
                                shipping_state: "",
                              }));
                              if (value.length === 6) fetchAddressFromPincode(value, "shipping");
                            }}
                            required />
                          {errors.shipping_pincode && <div className="error-text">{errors.shipping_pincode}</div>}
                        </div>

                        <div ref={(el) => (fieldRefs.current.shipping_city = el)} className="col-sm-6">
                          <label className="form-label required">City</label>
                          <input type="text" name="shipping_city"
                            maxLength="25"
                            className={`form-control themed-input ${errors.shipping_city ? "is-invalid" : ""}`}
                            value={formData.shipping_city} onChange={handleChange} disabled={!formData.shipping_manual_entry} />
                          {errors.shipping_city && <div className="error-text">{errors.shipping_city}</div>}
                        </div>

                        <div ref={(el) => (fieldRefs.current.shipping_state = el)} className="col-sm-6">
                          <label className="form-label required">State</label>
                          <input type="text" name="shipping_state"
                            maxLength="25"
                            className={`form-control themed-input ${errors.shipping_state ? "is-invalid" : ""}`}
                            value={formData.shipping_state} onChange={handleChange} disabled={!formData.shipping_manual_entry} />
                          {errors.shipping_state && <div className="error-text">{errors.shipping_state}</div>}
                        </div>
                      </>
                    )}

                  </div>

                  <hr className="my-4" />

                  <button
                    type="submit"
                    className="w-100 btn-themed btn-lg d-flex justify-content-center align-items-center"
                    disabled={
                      processingPayment ||
                      loadingShipping ||
                      loadingPincode
                    }
                  >
                    {submitLabel()}
                  </button>

                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      <style>{`
        :root {
          --brown-dark: #2E7D32;
          --brown-light: #f1e6d4;
          --cream-bg: #fffaf4;
          --error-color: #b3261e;
        }
        @keyframes pincodePulse {
          0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(179, 38, 30, 0.5); }
          50% { transform: scale(1.02); box-shadow: 0 0 0 6px rgba(179, 38, 30, 0.2); }
          100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(179, 38, 30, 0); }
        }
        .highlight-field {
          animation: pincodePulse 0.8s ease-in-out;
        }
        .error-text {
          color: var(--error-color);
          font-size: 0.85rem;
          margin-top: 2px;
          margin-left: 2px;
        }
        .is-invalid { border-color: var(--error-color) !important; }
        .form-label.required::after { content: " *"; color: red; font-weight: bold; }
        .thick-checkbox {
          width: 20px; height: 20px;
          border: 2.5px solid #4A1F1A !important;
          cursor: pointer;
          transition: all 0.25s ease;
        }
        .thick-checkbox:checked {
          background-color: #4A1F1A !important;
          border-color: #4A1F1A !important;
        }
        .thick-checkbox:focus { box-shadow: 0 0 0 0.15rem rgba(25,135,84,0.25); }
        .form-check-label { font-size: 0.95rem; color: #000; cursor: pointer; }
        @media (max-width: 768px) {
          .error-text { font-size: 0.8rem; }
          .thick-checkbox { width: 18px; height: 18px; border-width: 2px !important; }
        }
      `}</style>
    </>
  );
};

export default Checkout;